import { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import {
  CreditCard,
  Description,
  Person,
  Search,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import axios from '../../resources/axios';
import { formatDate } from '../../resources/functions';
import { selectCities } from '../../redux/slices/citiesSlice';

interface LiqpayOrderResult {
  payment_id: string;
  status: string;
  paytype: string;
  amount: number;
  receiver_commission?: number;
  sender_card_bank?: string;
  sender_card_country?: string;
  create_date: number;
  end_date?: number;
  description?: string;
}

interface Order {
  _id: string;
  type: string;
  city?: string;
  description?: string;
  createdAt: string;
  _receipted?: boolean;
}

interface User {
  firstName?: string;
  email?: string;
  phone?: string;
  city?: string;
  _indexBalance?: Record<string, number>;
  level?: number;
  mileage?: number;
  lastActive?: string;
  createdAt?: string;
}

interface PaymentInfo {
  liqpayOrderResult: LiqpayOrderResult;
  order: Order;
  user: User;
}

const PaymentSearch = () => {
  const theme = useTheme();
  const cities = useSelector(selectCities);
  const [paymentId, setPaymentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);

  const handleSearch = async () => {
    if (!paymentId.trim()) {
      setError('Введіть ID платежу');
      return;
    }

    setLoading(true);
    setError('');
    setPaymentInfo(null);

    try {
      const response = await axios.get(`/balance/info?paymentId=${paymentId}`);
      setPaymentInfo(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Платіж не знайдено ❌');
      } else {
        setError(err.response?.data?.message || 'Помилка отримання даних');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return formatDate(new Date(timestamp), { time: true });
  };

  const getCityName = (cityId: string | undefined) => {
    if (!cityId) return 'Не вказано';
    const city = cities.items.find(c => c._id === cityId);
    return city?.cityName || cityId;
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        sx={{
          p: 3,
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
          borderRadius: 2
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
            mb: 3
          }}
        >
          🔍 Пошук платежу за ID
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            type="number"
            placeholder="Введіть ID платежу..."
            value={paymentId}
            onChange={(e) => setPaymentId(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            disabled={loading}
            fullWidth
            sx={{ maxWidth: 400 }}
          />
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={loading}
            sx={{
              backgroundColor: '#5073F8',
              '&:hover': { backgroundColor: '#3d5dd6' }
            }}
          >
            Знайти платіж
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {paymentInfo && (
          <>
            <Grid container spacing={3}>
              {/* Інформація про платіж */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: paymentInfo.liqpayOrderResult.status !== 'success' 
                      ? 'rgba(244, 67, 54, 0.1)' 
                      : 'rgba(255, 222, 89, 0.2)',
                    border: paymentInfo.liqpayOrderResult.status !== 'success'
                      ? '2px solid #F44336'
                      : '1px solid #FFDE59',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CreditCard sx={{ fontSize: 32, color: '#FFDE59', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      💳 Інформація про платіж
                    </Typography>
                  </Box>

                  {paymentInfo.liqpayOrderResult.status !== 'success' && (
                    <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
                      Статус: {paymentInfo.liqpayOrderResult.status}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        ID транзакції
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {paymentInfo.liqpayOrderResult.payment_id}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        Статус
                      </Typography>
                      <Box>
                        <Chip
                          label={paymentInfo.liqpayOrderResult.status}
                          size="small"
                          sx={{
                            backgroundColor: paymentInfo.liqpayOrderResult.status === 'success' ? '#4CAF50' : '#F44336',
                            color: '#FFFFFF',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        Тип оплати
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {paymentInfo.liqpayOrderResult.paytype}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        Сума
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                        {paymentInfo.liqpayOrderResult.amount} грн
                      </Typography>
                    </Box>

                    {paymentInfo.liqpayOrderResult.receiver_commission && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Комісія отримувача
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {paymentInfo.liqpayOrderResult.receiver_commission} грн
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.liqpayOrderResult.sender_card_bank && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Банк відправника
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {paymentInfo.liqpayOrderResult.sender_card_bank}
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.liqpayOrderResult.sender_card_country && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Країна карти
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {paymentInfo.liqpayOrderResult.sender_card_country}
                        </Typography>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        Створено
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatTimestamp(paymentInfo.liqpayOrderResult.create_date)}
                      </Typography>
                    </Box>

                    {paymentInfo.liqpayOrderResult.end_date && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Завершено
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatTimestamp(paymentInfo.liqpayOrderResult.end_date)}
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.liqpayOrderResult.description && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Опис
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {paymentInfo.liqpayOrderResult.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Інформація про замовлення */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    border: '1px solid #2196F3',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Description sx={{ fontSize: 32, color: '#2196F3', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      🧾 Інформація про замовлення
                    </Typography>
                  </Box>

                  {paymentInfo.order._receipted && (
                    <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                      Квитанція отримана ✅
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        Order ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {paymentInfo.order._id}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        Тип
                      </Typography>
                      <Box>
                        <Chip
                          label={paymentInfo.order.type}
                          size="small"
                          sx={{
                            backgroundColor: paymentInfo.order.type === 'pay' ? '#4CAF50' : '#FF9800',
                            color: '#FFFFFF',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>

                    {paymentInfo.order.city && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Місто
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getCityName(paymentInfo.order.city)}
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.order.description && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Опис
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {paymentInfo.order.description}
                        </Typography>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        Дата створення
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDate(new Date(paymentInfo.order.createdAt), { time: true })}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Інформація про користувача */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: 'rgba(238, 238, 238, 0.3)',
                    border: theme.palette.mode === 'light' ? '1px solid #EEEEEE' : '1px solid #424242',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person sx={{ fontSize: 32, color: '#757575', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      👤 Інформація про користувача
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#757575' }}>
                        Ім'я / Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {paymentInfo.user.firstName || paymentInfo.user.email || 'Не вказано'}
                      </Typography>
                    </Box>

                    {paymentInfo.user.phone && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Телефон
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {paymentInfo.user.phone}
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.user.city && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Місто
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getCityName(paymentInfo.user.city)}
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.user._indexBalance && paymentInfo.user.city && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Баланс користувача
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                          {paymentInfo.user._indexBalance[paymentInfo.user.city] || 0} грн
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.user.level !== undefined && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Кількість поїздок (level)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {paymentInfo.user.level}
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.user.mileage !== undefined && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Загальний пробіг
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {paymentInfo.user.mileage} км
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.user.lastActive && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Остання активність
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(new Date(paymentInfo.user.lastActive), { time: true })}
                        </Typography>
                      </Box>
                    )}

                    {paymentInfo.user.createdAt && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Дата створення акаунта
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(new Date(paymentInfo.user.createdAt), { time: true })}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentSearch;
